/**
 * Device Fingerprinting Utility
 * Creates a unique identifier for each device/browser combination
 */

export interface DeviceFingerprint {
  fingerprint: string;
  metadata: DeviceMetadata;
}

export interface DeviceMetadata {
  screenResolution: string;
  timezone: string;
  language: string;
  platform: string;
  userAgent: string;
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory?: number;
  touchSupport: boolean;
  webglVendor?: string;
  webglRenderer?: string;
  plugins: string[];
  fonts: string[];
}

/**
 * Generate a unique device fingerprint
 */
export async function generateDeviceFingerprint(): Promise<DeviceFingerprint> {
  const metadata = await collectDeviceMetadata();
  const fingerprint = await createFingerprint(metadata);
  
  return { fingerprint, metadata };
}

/**
 * Collect device metadata
 */
async function collectDeviceMetadata(): Promise<DeviceMetadata> {
  const metadata: DeviceMetadata = {
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    colorDepth: screen.colorDepth,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: 'deviceMemory' in navigator ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    webglVendor: getWebGLVendor(),
    webglRenderer: getWebGLRenderer(),
    plugins: getPlugins(),
    fonts: await getInstalledFonts(),
  };

  return metadata;
}

/**
 * Create a stable fingerprint hash from metadata
 */
async function createFingerprint(metadata: DeviceMetadata): Promise<string> {
  // Select stable attributes for fingerprinting
  const fingerprintData = [
    metadata.screenResolution,
    metadata.timezone,
    metadata.language,
    metadata.platform,
    metadata.colorDepth,
    metadata.hardwareConcurrency,
    metadata.touchSupport,
    metadata.webglVendor,
    metadata.webglRenderer,
    // Don't include userAgent as it changes frequently
    // Don't include plugins/fonts as they can change
  ].filter(Boolean).join('|');

  return await hashString(fingerprintData);
}

/**
 * Get WebGL vendor information
 */
function getWebGLVendor(): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
      }
    }
  } catch (e) {
    console.error('Failed to get WebGL vendor:', e);
  }
  return undefined;
}

/**
 * Get WebGL renderer information
 */
function getWebGLRenderer(): string | undefined {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
      }
    }
  } catch (e) {
    console.error('Failed to get WebGL renderer:', e);
  }
  return undefined;
}

/**
 * Get browser plugins
 */
function getPlugins(): string[] {
  try {
    if (navigator.plugins && navigator.plugins.length > 0) {
      return Array.from(navigator.plugins)
        .map(plugin => plugin.name)
        .slice(0, 10); // Limit to first 10 plugins
    }
  } catch (e) {
    console.error('Failed to get plugins:', e);
  }
  return [];
}

/**
 * Detect installed fonts (limited set for performance)
 */
async function getInstalledFonts(): Promise<string[]> {
  const testFonts = [
    'Arial', 'Verdana', 'Times New Roman', 'Courier New',
    'Georgia', 'Palatino', 'Garamond', 'Bookman',
    'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact'
  ];
  
  const detectedFonts: string[] = [];
  
  try {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    if (!context) return [];
    
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    
    const baseFontWidths: Record<string, number> = {};
    for (const baseFont of baseFonts) {
      context.font = `${testSize} ${baseFont}`;
      baseFontWidths[baseFont] = context.measureText(testString).width;
    }
    
    for (const font of testFonts) {
      let detected = false;
      
      for (const baseFont of baseFonts) {
        context.font = `${testSize} '${font}', ${baseFont}`;
        const width = context.measureText(testString).width;
        
        if (width !== baseFontWidths[baseFont]) {
          detected = true;
          break;
        }
      }
      
      if (detected) {
        detectedFonts.push(font);
      }
    }
  } catch (e) {
    console.error('Failed to detect fonts:', e);
  }
  
  return detectedFonts;
}

/**
 * Hash a string using SHA-256
 */
async function hashString(str: string): Promise<string> {
  const msgBuffer = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Get device type from user agent
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const ua = navigator.userAgent.toLowerCase();
  
  if (/mobile|android|iphone|ipod/.test(ua) && !/ipad|tablet/.test(ua)) {
    return 'mobile';
  }
  
  if (/ipad|tablet|playbook|silk/.test(ua)) {
    return 'tablet';
  }
  
  return 'desktop';
}

/**
 * Get browser name from user agent
 */
export function getBrowserName(): string {
  const ua = navigator.userAgent;
  
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  
  return 'Unknown';
}

/**
 * Format device name for display
 */
export function formatDeviceName(metadata: DeviceMetadata): string {
  const deviceType = getDeviceType();
  const browser = getBrowserName();
  const platform = metadata.platform;
  
  return `${browser} on ${platform} (${deviceType})`;
}